import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, Loader2, Upload, X } from 'lucide-react';
import { uploadToCloudinary } from '../lib/api';
import { toast } from 'sonner';

/**
 * Editor de variações de produto — EXCLUSIVO do Admin DESTACK Venda Mais.
 * Cada variação é um grupo (ex.: Cor, Tamanho, Sabor) com múltiplas opções.
 * Cada opção pode ter ajuste de preço (+/-) e imagem própria.
 */
const uid = () => Math.random().toString(36).slice(2, 10);

export const ProductVariationsEditor = ({ value, onChange }) => {
  const variations = value || [];
  const [uploadingKey, setUploadingKey] = useState(null);

  const emit = (next) => onChange?.(next);

  const addGroup = () => {
    emit([
      ...variations,
      {
        id: uid(),
        name: '',
        options: [{ id: uid(), label: '', price_delta: 0, image_url: '' }],
      },
    ]);
  };

  const removeGroup = (gid) => emit(variations.filter((g) => g.id !== gid));

  const updateGroup = (gid, patch) =>
    emit(variations.map((g) => (g.id === gid ? { ...g, ...patch } : g)));

  const addOption = (gid) =>
    updateGroup(gid, {
      options: [
        ...(variations.find((g) => g.id === gid)?.options || []),
        { id: uid(), label: '', price_delta: 0, image_url: '' },
      ],
    });

  const updateOption = (gid, oid, patch) => {
    const group = variations.find((g) => g.id === gid);
    if (!group) return;
    updateGroup(gid, {
      options: group.options.map((o) => (o.id === oid ? { ...o, ...patch } : o)),
    });
  };

  const removeOption = (gid, oid) => {
    const group = variations.find((g) => g.id === gid);
    if (!group) return;
    updateGroup(gid, { options: group.options.filter((o) => o.id !== oid) });
  };

  const handleImage = async (gid, oid, file) => {
    if (!file) return;
    const key = `${gid}:${oid}`;
    setUploadingKey(key);
    try {
      const url = await uploadToCloudinary(file, 'image', 'products/variations');
      updateOption(gid, oid, { image_url: url });
      toast.success('Imagem da variação atualizada.');
    } catch (e) {
      toast.error('Falha ao enviar imagem.');
    } finally {
      setUploadingKey(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="variations-editor">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-bold uppercase tracking-widest text-blue-700">
            Variações do Produto
          </Label>
          <p className="text-xs text-slate-500 mt-1">
            Cadastre grupos como Cor, Tamanho, Sabor, Modelo, Peso. Cada opção pode ter
            ajuste de preço (+/-) e imagem própria.
          </p>
        </div>
        <Button
          type="button"
          onClick={addGroup}
          variant="outline"
          size="sm"
          className="border-blue-200 text-blue-700"
          data-testid="add-variation-group-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Adicionar grupo
        </Button>
      </div>

      {variations.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
          Este produto não possui variações. Clique em <strong>Adicionar grupo</strong> se
          precisar cadastrar cores, tamanhos, sabores etc.
        </div>
      )}

      {variations.map((group, gi) => (
        <div
          key={group.id}
          className="rounded-xl border border-slate-200 bg-white p-4 space-y-3"
          data-testid={`variation-group-${gi}`}
        >
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs">Nome do grupo *</Label>
              <Input
                value={group.name}
                onChange={(e) => updateGroup(group.id, { name: e.target.value })}
                placeholder="Ex.: Cor, Tamanho, Sabor"
                data-testid={`variation-group-name-${gi}`}
              />
            </div>
            <Button
              type="button"
              onClick={() => removeGroup(group.id)}
              variant="ghost"
              size="sm"
              className="text-red-600 hover:bg-red-50"
              data-testid={`remove-variation-group-${gi}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            {group.options.map((opt, oi) => {
              const uploading = uploadingKey === `${group.id}:${opt.id}`;
              return (
                <div
                  key={opt.id}
                  className="grid grid-cols-[64px_1fr_120px_40px] gap-2 items-center"
                  data-testid={`variation-option-${gi}-${oi}`}
                >
                  <div className="relative w-16 h-16 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                    {opt.image_url ? (
                      <>
                        <img src={opt.image_url} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => updateOption(group.id, opt.id, { image_url: '' })}
                          className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                          data-testid={`remove-variation-img-${gi}-${oi}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center text-slate-400 text-[10px]">
                        {uploading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            <span>Foto</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImage(group.id, opt.id, e.target.files?.[0])}
                          disabled={uploading}
                          data-testid={`upload-variation-img-${gi}-${oi}`}
                        />
                      </label>
                    )}
                  </div>
                  <Input
                    value={opt.label}
                    onChange={(e) => updateOption(group.id, opt.id, { label: e.target.value })}
                    placeholder="Ex.: Preto, Chocolate, P, GG"
                    data-testid={`variation-option-label-${gi}-${oi}`}
                  />
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">R$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={opt.price_delta}
                      onChange={(e) => updateOption(group.id, opt.id, { price_delta: parseFloat(e.target.value) || 0 })}
                      className="pl-8"
                      placeholder="0.00"
                      data-testid={`variation-option-delta-${gi}-${oi}`}
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => removeOption(group.id, opt.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 w-10 h-10 p-0"
                    data-testid={`remove-variation-option-${gi}-${oi}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
            <Button
              type="button"
              onClick={() => addOption(group.id)}
              variant="ghost"
              size="sm"
              className="text-blue-700"
              data-testid={`add-variation-option-${gi}`}
            >
              <Plus className="w-4 h-4 mr-1" /> Adicionar opção
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">
            Ajuste (R$): valores positivos aumentam, negativos reduzem o preço base.
          </p>
        </div>
      ))}
    </div>
  );
};

export default ProductVariationsEditor;
